import { useState, useEffect, useCallback } from 'react';
import { productsAPI } from '../services/api';

export function withUpdatedInventory(product, quantity) {
  if (!product?.variants?.length) return product;
  return {
    ...product,
    variants: product.variants.map((variant, index) =>
      index === 0 ? { ...variant, inventory_quantity: quantity } : variant
    )
  };
}

const useProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await productsAPI.getProducts();
      setProducts(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Ürünler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter products based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [products, searchTerm]);

  // Update single product
  const updateProduct = useCallback(async (productId, productData) => {
    try {
      await productsAPI.updateProduct(productId, productData);
      await fetchProducts(); // Refresh products list
      return { success: true };
    } catch (err) {
      return { 
        success: false, 
        error: err.response?.data?.error || 'Ürün güncelleme hatası' 
      };
    }
  }, [fetchProducts]);

  const applyLocalInventory = useCallback((productId, quantity) => {
    setProducts((prev) =>
      prev.map((product) =>
        product.id === productId ? withUpdatedInventory(product, quantity) : product
      )
    );
  }, []);

  const applyLocalInventories = useCallback((updates) => {
    if (!updates?.length) return;
    const byId = new Map(updates.map((u) => [u.productId ?? u.id, u.quantity]));
    setProducts((prev) =>
      prev.map((product) =>
        byId.has(product.id)
          ? withUpdatedInventory(product, byId.get(product.id))
          : product
      )
    );
  }, []);

  const updateInventory = useCallback(async (productId, quantity) => {
    try {
      const response = await productsAPI.updateInventory(productId, quantity);
      const savedQty = response.data?.quantity ?? quantity;
      // products.json stoğu gecikmeli gelebilir; kaydettiğimiz değeri hemen yaz
      applyLocalInventory(productId, savedQty);
      return { success: true, data: { ...response.data, quantity: savedQty } };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.error || 'Stok güncelleme hatası'
      };
    }
  }, [applyLocalInventory]);

  const bulkUpdateInventory = useCallback(async (items) => {
    try {
      const response = await productsAPI.bulkUpdateInventory(items);
      const results = response.data?.results || [];
      if (results.length > 0) {
        applyLocalInventories(results);
      } else {
        applyLocalInventories(items);
      }
      return { success: response.data?.success !== false, data: response.data };
    } catch (err) {
      const timedOut = err.code === 'ECONNABORTED';
      return {
        success: false,
        error:
          err.response?.data?.error ||
          (timedOut
            ? 'Toplu stok güncellemesi zaman aşımına uğradı. Daha az ürün seçip tekrar deneyin.'
            : 'Toplu stok güncelleme hatası')
      };
    }
  }, [applyLocalInventories]);

  // Bulk percentage change
  const bulkPercentageChange = useCallback(async (productIds, percentage, type) => {
    try {
      const response = await productsAPI.bulkPercentageChange({
        productIds,
        percentage,
        type
      });
      await fetchProducts(); // Refresh products list
      return { success: true, data: response.data };
    } catch (err) {
      return { 
        success: false, 
        error: err.response?.data?.error || 'Toplu fiyat değişimi hatası' 
      };
    }
  }, [fetchProducts]);

  // Bulk update products
  const bulkUpdateProducts = useCallback(async (productsToUpdate) => {
    try {
      const response = await productsAPI.bulkUpdateProducts(productsToUpdate);
      await fetchProducts(); // Refresh products list
      return { success: true, data: response.data };
    } catch (err) {
      return { 
        success: false, 
        error: err.response?.data?.error || 'Toplu güncelleme hatası' 
      };
    }
  }, [fetchProducts]);

  // Initial fetch
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return {
    products: filteredProducts,
    allProducts: products,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    fetchProducts,
    updateProduct,
    updateInventory,
    bulkUpdateInventory,
    bulkPercentageChange,
    bulkUpdateProducts
  };
};

export default useProducts;
